import type { ButtonHTMLAttributes, ReactNode } from 'react';

import { cx } from '../lib/cx.ts';
import { Spinner } from './Spinner.tsx';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';

type ButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> & {
  variant?: Variant;
  /** Fills the width of its container. Use for the primary action on a form. */
  block?: boolean;
  /** Shows a spinner and disables the button. Never tie this to a sync call. */
  loading?: boolean;
  children: ReactNode;
  className?: string;
};

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-accent text-accent-contrast hover:bg-accent-hover',
  secondary: 'bg-surface-raised text-text border border-line hover:border-line-strong',
  ghost: 'bg-transparent text-muted hover:bg-surface-raised hover:text-text',
  danger: 'bg-danger text-danger-contrast hover:bg-danger-hover',
};

export function Button({
  variant = 'secondary',
  block = false,
  loading = false,
  disabled = false,
  type = 'button',
  children,
  className,
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cx(
        'inline-flex min-h-touch items-center justify-center gap-2 rounded-md px-4',
        /*
         * `max-w-full` and a wrapping label rather than `whitespace-nowrap`:
         * an inline-flex button sizes to its text, so a long label at a large
         * text size grew past its card and pushed the whole page sideways.
         * At ordinary sizes the labels still fit on one line — this only
         * takes effect when the alternative is overflowing.
         */
        'max-w-full text-center text-base font-medium',
        'transition-colors duration-(--ui-duration-fast)',
        'disabled:pointer-events-none disabled:opacity-50',
        VARIANTS[variant],
        block && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner size="sm" label="Working" /> : null}
      {children}
    </button>
  );
}
